import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave with a visible status.
 *
 * There is no Save button anywhere in this app. A Save button means work can be
 * lost by closing the wrong thing, and remembering to press it is exactly the
 * kind of overhead this product exists to remove.
 *
 * Text fields debounce so every keystroke is not a request; toggles and
 * segmented controls call `flush` and save immediately, because the user has
 * already finished expressing the change.
 */
export function useAutosave<T>(
  save: (patch: Partial<T>) => Promise<unknown>,
  delay = 600,
) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const pending = useRef<Partial<T>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const commit = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    const patch = pending.current;
    if (Object.keys(patch).length === 0) return;
    pending.current = {};

    setStatus('saving');
    try {
      await save(patch);
      if (mounted.current) {
        setStatus('saved');
        // Fade the indicator rather than leaving "Saved" on screen forever.
        setTimeout(() => mounted.current && setStatus('idle'), 1600);
      }
    } catch {
      if (mounted.current) setStatus('error');
    }
  }, [save]);

  /** Queue a change. Debounced unless `immediate`. */
  const push = useCallback(
    (patch: Partial<T>, immediate = false) => {
      pending.current = { ...pending.current, ...patch };
      if (timer.current) clearTimeout(timer.current);

      if (immediate) {
        void commit();
        return;
      }
      timer.current = setTimeout(() => void commit(), delay);
    },
    [commit, delay],
  );

  // Anything typed and not yet saved must survive the sheet closing.
  const flush = useCallback(() => void commit(), [commit]);

  return { status, push, flush };
}
